// One async mutex per engine so pull and push never interleave (SyncEngine.acquire/release,
// GroundTeam/Sync/SyncEngine.swift:164-173). A pull applying a page while a push is processing its result would
// merge against a base the push is about to replace; iOS serialises them, and so does the web.

export class AsyncGate {
  private busy = false;
  private waiters: (() => void)[] = [];

  /** True while a task holds the gate. */
  get isBusy(): boolean {
    return this.busy;
  }

  /** Waiting tasks (not counting the one running). */
  get queued(): number {
    return this.waiters.length;
  }

  /** Runs `task` once every earlier task has finished (FIFO). The gate is released even when `task` throws. */
  async run<T>(task: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await task();
    } finally {
      this.release();
    }
  }

  private acquire(): Promise<void> {
    if (!this.busy) {
      this.busy = true;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => this.waiters.push(resolve));
  }

  private release(): void {
    const next = this.waiters.shift();
    if (next) next();
    else this.busy = false;
  }
}
